import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import Sidebar from "@/components/Sidebar";
import ConfirmDialog from "@/components/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { KeyRound, Plus, Copy, Trash2, AlertTriangle, CheckCircle2, ShieldOff } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MCP_URL = `${BACKEND_URL}/api/mcp/`;

const ApiKeys = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [newKeyName, setNewKeyName] = useState("");
  const [allowSubscriptionCreation, setAllowSubscriptionCreation] = useState(false);
  const [allowSubscriptionEditing, setAllowSubscriptionEditing] = useState(false);
  const [creating, setCreating] = useState(false);
  // After creation, the plaintext key is held here ONCE so the user can copy
  // it. Cleared when the "I've saved it" dialog closes — we never refetch it.
  const [revealedKey, setRevealedKey] = useState(null);
  const [revokeConfirm, setRevokeConfirm] = useState({ open: false, keyId: null, keyName: "" });
  const token = localStorage.getItem("token");

  const fetchKeys = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api-keys`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setKeys(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("Access denied — managers only");
        navigate("/dashboard");
      } else if (error.response?.status === 401) {
        localStorage.removeItem("token");
        setUser(null);
        navigate("/login");
      }
    } finally {
      setLoading(false);
    }
  }, [token, navigate, setUser]);

  useEffect(() => {
    fetchKeys();
  }, [fetchKeys]);

  const handleLogout = () => {
    localStorage.removeItem("token");
    setUser(null);
    navigate("/login");
  };

  const handleCreate = async () => {
    const name = newKeyName.trim();
    if (!name) {
      toast.error("Give the key a name");
      return;
    }
    setCreating(true);
    try {
      const response = await axios.post(
        `${API}/api-keys`,
        { name, allow_subscription_creation: allowSubscriptionCreation, allow_subscription_editing: allowSubscriptionEditing },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setRevealedKey(response.data);
      setCreateModalOpen(false);
      setNewKeyName("");
      setAllowSubscriptionCreation(false);
      setAllowSubscriptionEditing(false);
      fetchKeys();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create key");
    } finally {
      setCreating(false);
    }
  };

  const handleCopy = (text, label = "Copied to clipboard") => {
    navigator.clipboard.writeText(text);
    toast.success(label);
  };

  const handleRevoke = async (keyId) => {
    try {
      await axios.delete(`${API}/api-keys/${keyId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("API key revoked");
      fetchKeys();
    } catch (error) {
      toast.error("Failed to revoke key");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "Never";
    return new Date(dateStr).toLocaleString("en-AU", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex">
        <Sidebar user={user} setUser={setUser} onLogout={handleLogout} />
        <div className="flex-1 flex items-center justify-center ml-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
        </div>
      </div>
    );
  }

  const activeKeys = keys.filter((k) => !k.revoked);

  return (
    <div className="min-h-screen bg-slate-50 flex" data-testid="api-keys-page">
      <Sidebar user={user} setUser={setUser} onLogout={handleLogout} />
      <div className="flex-1 p-8 ml-64">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">API Keys</h1>
            <p className="text-slate-500 mt-1">
              Connect Claude.ai, ChatGPT, or Claude Desktop to your Storm Pad org via MCP.
            </p>
          </div>
          <Button
            onClick={() => setCreateModalOpen(true)}
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
            data-testid="create-key-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create API Key
          </Button>
        </div>

        {/* Connection details — what to plug into the MCP client */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>How to connect</CardTitle>
            <CardDescription>
              Paste the URL below into your MCP client's "Add custom connector" flow,
              and use one of your API keys as the bearer token. The connector reads
              your org's subscriptions, leads, and sales metrics. New keys can optionally allow creating or editing subscriptions; existing permissions stay unchanged.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
              <code className="flex-1 text-sm font-mono text-slate-700 break-all">{MCP_URL}</code>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleCopy(MCP_URL, "MCP URL copied")}
                data-testid="copy-mcp-url"
              >
                <Copy className="w-4 h-4 mr-1" />
                Copy
              </Button>
            </div>
            <p className="text-xs text-slate-500 mt-3">
              Available tools: search_subscriptions, get_subscription, get_sales_dashboard,
              get_renewals_due, search_leads, count_subscriptions, count_leads. With creation permission: create_subscription. With editing permission: update_subscription.
            </p>
          </CardContent>
        </Card>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-slate-500">Total Keys</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{keys.length}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-emerald-600">Active</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-emerald-600">{activeKeys.length}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-slate-500">Revoked</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{keys.length - activeKeys.length}</p>
            </CardContent>
          </Card>
        </div>

        {/* Keys list */}
        <Card>
          <CardHeader>
            <CardTitle>All API Keys</CardTitle>
            <CardDescription>
              Keys are scoped to this organization. Revoke immediately if a key leaks.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {keys.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <KeyRound className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No API keys yet</p>
                <p className="text-sm">Create one to connect Claude.ai or ChatGPT to Storm Pad</p>
              </div>
            ) : (
              <div className="space-y-3">
                {keys.map((k) => (
                  <div
                    key={k.key_id}
                    className={`p-4 rounded-lg border ${
                      k.revoked ? "bg-slate-50 border-slate-200" : "bg-white border-slate-200"
                    }`}
                    data-testid={`key-${k.key_id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4 min-w-0 flex-1">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium text-slate-900 truncate">{k.name}</span>
                            {k.revoked ? (
                              <Badge className="bg-slate-100 text-slate-600">
                                <ShieldOff className="w-3 h-3 mr-1" />
                                Revoked
                              </Badge>
                            ) : (
                              <Badge className="bg-emerald-100 text-emerald-700">
                                <CheckCircle2 className="w-3 h-3 mr-1" />
                                Active
                              </Badge>
                            )}
                          </div>
                          <code className="text-xs font-mono text-slate-500">
                            {k.key_prefix}…
                          </code>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {!k.revoked && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setRevokeConfirm({ open: true, keyId: k.key_id, keyName: k.name })}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid={`revoke-key-${k.key_id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                    <div className="mt-2 text-xs text-slate-500 flex flex-wrap gap-x-4 gap-y-1">
                      <span>Created: {formatDate(k.created_at)}</span>
                      <span>Last used: {formatDate(k.last_used_at)}</span>
                      <span>Owner: {k.user_name || k.user_email || "—"}</span>
                      <span>Access: {(k.scope || "mcp").split(" ").includes("mcp:subscriptions:create") ? "Read and create subscriptions" : "Read only"}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Create modal */}
        <Dialog open={createModalOpen} onOpenChange={setCreateModalOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create API Key</DialogTitle>
              <DialogDescription>
                Give the key a label so you can recognise it later (e.g. "Claude Desktop"
                or "ChatGPT — laptop"). The key itself will only be shown once.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="key-name">Name</Label>
                <Input
                  id="key-name"
                  placeholder="e.g. Claude Desktop"
                  value={newKeyName}
                  onChange={(e) => setNewKeyName(e.target.value)}
                  data-testid="key-name-input"
                  autoFocus
                />
              </div>
              <label className="flex items-start gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={allowSubscriptionCreation}
                  onChange={e => setAllowSubscriptionCreation(e.target.checked)}
                  data-testid="key-allow-subscription-creation" />
                <span>Allow creating customer subscriptions, including payment status, notes and renewals.
                  Existing subscriptions cannot be edited or deleted with this permission.</span>
              </label>
              <label className="flex items-start gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={allowSubscriptionEditing}
                  onChange={e => setAllowSubscriptionEditing(e.target.checked)}
                  data-testid="key-allow-subscription-editing" />
                <span>Allow editing existing customer subscriptions, including payment status, notes and renewals.</span>
              </label>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateModalOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleCreate}
                disabled={creating}
                className="bg-indigo-600 hover:bg-indigo-700 text-white"
                data-testid="confirm-create-key-btn"
              >
                {creating ? "Creating…" : "Create Key"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reveal-once dialog — the only time the plaintext key is ever shown */}
        <Dialog
          open={!!revealedKey}
          onOpenChange={(open) => {
            if (!open) setRevealedKey(null);
          }}
        >
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <KeyRound className="w-5 h-5 text-emerald-600" />
                Your new API key
              </DialogTitle>
              <DialogDescription>
                Copy this now — you won't be able to see it again. Treat it like a
                password.
              </DialogDescription>
            </DialogHeader>
            <Alert variant="destructive" className="border-amber-300 bg-amber-50 text-amber-900">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <AlertTitle>Save it before closing</AlertTitle>
              <AlertDescription>
                After this dialog closes, the full key is gone. If you lose it, revoke
                this key and create a new one.
              </AlertDescription>
            </Alert>
            <div className="space-y-2">
              <Label>Key</Label>
              <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                <code className="flex-1 text-sm font-mono text-slate-900 break-all">
                  {revealedKey?.key}
                </code>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleCopy(revealedKey.key, "Key copied — paste it into your MCP client now")}
                  data-testid="copy-revealed-key"
                >
                  <Copy className="w-4 h-4 mr-1" />
                  Copy
                </Button>
              </div>
            </div>
            <DialogFooter>
              <Button
                onClick={() => setRevealedKey(null)}
                className="bg-indigo-600 hover:bg-indigo-700 text-white"
                data-testid="dismiss-revealed-key"
              >
                I've saved it
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Revoke confirmation */}
        <ConfirmDialog
          open={revokeConfirm.open}
          onOpenChange={(open) => setRevokeConfirm({ open, keyId: null, keyName: "" })}
          title={`Revoke "${revokeConfirm.keyName}"?`}
          description="Any MCP client using this key will immediately lose access. This cannot be undone — you'll need to create a new key to reconnect."
          confirmText="Revoke"
          variant="destructive"
          onConfirm={() => handleRevoke(revokeConfirm.keyId)}
        />
      </div>
    </div>
  );
};

export default ApiKeys;
